import React from 'react';

import {
  Box, Center, HStack, VStack, Icon,
  Spinner, Text, ScrollView, Button, ButtonText, ButtonIcon,
} from '@gluestack-ui/themed';

import { useNavigation, useIsFocused } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import {
  CircleCheckBig, Pencil, AlertCircle, Lock,
} from 'lucide-react-native';
import { Task, TaskScope, TaskTypeEnum } from '@/student/types';
import { useAuthStore } from '@/common/auth/store';
import useUnsavedLeaveGuard from '@/common/hooks/useUnsavedLeaveGuard';
// NOTE: These hooks are generated by yarn orval
import {
  useListTaskSubmissionHistory,
  getTaskSubmissionVersion,
} from '../../../common/api/endpoints/submissions/submissions';
import { useAutosave } from '../../../common/hooks/forms/useAutosave';

import { useDialog } from '../../../common/context/DialogContext';
import { useAppStateStore } from '../../../common/context/AppStateStore';
import { useAttachmentMutations } from '../../../common/hooks/student/task/useAttachmentMutations';

import EditorToolbar from './components/EditorToolbar';
import BottomPanel from './components/BottomPanel';
import EditorShell, { QuillHandle } from './components/EditorShell';
import useEditorSync from './hooks/useEditorSync';
import { useTaskSubmissionActions, useTaskSubmissionState } from '../stores/TaskSubmissionStore';
// eslint-disable-next-line import/no-named-as-default
import useTaskSubmissionController from '../hooks/useTaskSubmissionController';

import AttachmentsPanel from '../AttachmentsPanel';
import { VersionHistoryModal, TaskSubmissionVersionSummary } from '../components/VersionHistory/VersionHistoryModal';
import HtmlText from '../../../common/components/HtmlText';

/**
 * Internal component to handle unsaved leave guard.
 * Conditionally rendered to avoid calling navigation hooks in preview mode.
 */
const UnsavedLeaveGuard = ({
  isDirty,
  isAutosaveInFlight,
  showDialog,
  navigation,
  t,
}: {
  isDirty: boolean;
  isAutosaveInFlight: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  showDialog: (props: any) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  navigation: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: (key: string, options?: any) => string;
}) => {
  useUnsavedLeaveGuard({
    isDirty,
    isAutosaveInFlight,
    showDialog,
    navigation,
    labels: {
      title: t('student.task.composer.unsaved-dialog.title'),
      message: t('student.task.composer.unsaved-dialog.message'),
      stay: t('student.task.composer.unsaved-dialog.button-stay'),
      discard: t('student.task.composer.unsaved-dialog.button-discard'),
    },
  });
  return null;
};

export interface ComposerProps {
  task: Task;
  readonly?: boolean;
  testID?: string;
  isMobileView?: boolean;
  isLoading?: boolean;
  taskScope: TaskScope;
  safetyFuseConflict?: boolean;
}

const EssayComposer: React.FC<ComposerProps> = ({
  task,
  readonly = false,
  testID = 'response-composer',
  isMobileView = false,
  isLoading = false,
  taskScope,
  safetyFuseConflict = false,
}) => {
  const selectedModule = useAppStateStore((state) => state.selectedModule);
  const isTasksLoading = isLoading;
  const editorRef = React.useRef<QuillHandle>(null);
  const isMarkedAsDone = task?.submission?.state === 'marked_as_done';
  const [isEditingOverride, setIsEditingOverride] = React.useState(false);

  const { t } = useTranslation();
  const isTrialStudent = useAuthStore((state) => Boolean(state.user?.trial?.isTrial));
  const { showDialog, showToastMessage } = useDialog();
  const navigation = useNavigation();
  const isFocused = useIsFocused();

  const taskId = task.id;
  const { courseId } = taskScope;
  const { moduleId } = taskScope;

  // Version history state and hooks
  const [showHistoryModal, setShowHistoryModal] = React.useState(false);
  const {
    data: historyData,
    isLoading: isHistoryLoading,
    isError: isHistoryError,
  } = useListTaskSubmissionHistory(courseId, moduleId, taskId, undefined, {
    query: { enabled: showHistoryModal, staleTime: 10000 },
  });

  // Controller for save/submit orchestration (explicit ids)
  const {
    submit, evaluate, saveDraft,
  } = useTaskSubmissionController({
    taskScope: {
      courseId,
      moduleId: task.moduleId || moduleId,
      taskId,
    },
    taskType: task.type as TaskTypeEnum,
    formalReq: task.evaluation?.formalCriteria,
  });

  const handleReopen = React.useCallback(async () => {
    setIsEditingOverride(true);
    try {
      // Transition back to draft
      await saveDraft(undefined, { silent: true });
    } catch (error) {
      // Revert if save failed
      setIsEditingOverride(false);
    }
  }, [saveDraft]);

  const { setDirty, setContentAndWordcount } = useTaskSubmissionActions();
  const {
    dirty,
    content,
    wordcount,
    canSubmit,
    wordcountColor,
    wordcountTargets,
    isEvaluating,
    isUploading,
    autosave: storeAutosave,
    lastSavedAt,
  } = useTaskSubmissionState();

  // Autosave - only when screen is focused to prevent ghost instances from firing
  useAutosave({
    saveFunction: async () => {
      await saveDraft(content || '', { silent: true });
    },
    isDirty: dirty,
    watchValue: content,
    enabled: isFocused && !isTasksLoading && !readonly && !safetyFuseConflict,
    debounceMs: 2000,
  });

  // Compact Save Label
  const { compactSaveLabel, compactSaveColor } = React.useMemo(() => {
    if (storeAutosave.status === 'inflight') {
      return { compactSaveLabel: 'Saving...', compactSaveColor: '$textSubtle' };
    }
    if (storeAutosave.status === 'error') {
      return { compactSaveLabel: 'Error', compactSaveColor: '$statusError' };
    }
    if (dirty) {
      return { compactSaveLabel: 'Unsaved', compactSaveColor: '$textSubtle' };
    }
    if (storeAutosave.status === 'saved') {
      return { compactSaveLabel: 'Saved', compactSaveColor: '$statusSuccess' };
    }
    return { compactSaveLabel: '', compactSaveColor: '$textSubtle' };
  }, [storeAutosave.status, dirty]);

  // Load submission content into editor and disable while loading
  const { status: editorStatus, retryCount: editorRetryCount } = useEditorSync({
    editorRef,
    isTasksLoading,
    submissionText: content,
    taskId: task?.id,
  });

  const showWordcountDialog = React.useCallback(() => {
    showDialog({
      title: t('student.task.composer.wordcount.title'),
      message: t('student.task.composer.wordcount.message', {
        bodyWords: wordcount.bodyWords,
        allWords: wordcount.allWords,
      }),
      buttons: [{ text: t('common.buttons.ok') }],
    });
  }, [wordcount, showDialog, t]);

  const handleLoadVersion = React.useCallback(
    async (versionSeq: number) => {
      try {
        const response = await getTaskSubmissionVersion(
          taskScope.courseId,
          taskScope.moduleId,
          taskScope.taskId,
          versionSeq,
        );

        const versionContent = response?.data?.data?.content || '';

        // Update editor content via ref if available
        if (editorRef.current?.setHtml) {
          await editorRef.current.setHtml(versionContent);
        }

        // Update store state
        setContentAndWordcount(versionContent);
        setDirty(true);

        showToastMessage(
          'success',
          t('student.task.versionHistory.loaded', 'Version {{seq}} loaded. Changes will auto-save in a few seconds.', {
            seq: versionSeq,
          }),
        );
      } catch (error) {
        showToastMessage('error', t('student.task.versionHistory.loadError', 'Failed to load version'));
      }
    },
    [taskScope, setContentAndWordcount, setDirty, showToastMessage, t],
  );

  const { uploadAttachmentMutation } = useAttachmentMutations();

  const handleShowHistory = React.useCallback(() => {
    setShowHistoryModal(true);
  }, []);

  // Desktop Event Task Guard
  React.useEffect(() => {
    setIsEditingOverride(false);
  }, [task?.id]);

  if (isTasksLoading || !task) {
    return (
      <Center testID={`${testID}-loading`}>
        <Spinner size="small" />
      </Center>
    );
  }

  const gatedMode = task?.evaluation?.completionPolicyEffective === 'gated';
  const autoEval = task?.evaluation?.mode === 'auto';
  const isDraft = task?.submission?.state === 'draft';
  const showReadonlyView = isMarkedAsDone && !isEditingOverride;

  return (
    <Box flex={1} testID={testID}>
      {isTrialStudent && (
        <HStack
          testID="trial-read-only-notice"
          alignItems="center"
          space="sm"
          p="$3"
          mb="$3"
          bg="$bgSubtle"
          borderRadius="$md"
        >
          <Icon as={Lock} size="sm" color="$textSubtle" />
          <Text flex={1} size="sm" color="$textBody">
            {t('student.trial.readOnlyNotice')}
          </Text>
        </HStack>
      )}
      {!readonly && (
        <UnsavedLeaveGuard
          isDirty={dirty}
          isAutosaveInFlight={storeAutosave.status === 'inflight'}
          showDialog={showDialog}
          navigation={navigation}
          t={t}
        />
      )}
      {!showReadonlyView && (
        <EditorToolbar
          disableButtons={
            uploadAttachmentMutation.isPending
            || readonly
            || isTasksLoading
            || isEvaluating
            || isUploading
          }
          onEvaluate={() => evaluate()}
          onSubmit={() => submit()}
          canSubmit={canSubmit}
          showEvaluate={isDraft && (gatedMode || autoEval)}
          showSubmitButton={isDraft && !gatedMode}
          showWordcountInline={isMobileView}
          wordcount={wordcount}
          targetWordcount={wordcountTargets?.target}
          wordcountColor={wordcountColor}
          compactSaveLabel={compactSaveLabel}
          compactSaveColor={compactSaveColor}
          onRetry={() => saveDraft(undefined, { silent: false, showErrorDialog: true })}
        />
      )}

      {/* Editor or Readonly view with Attachments Panel and Bottom Panel (Wordcount and Save Status) */}

      <VStack flex={1}>
        {!showReadonlyView && (editorStatus === 'syncing' && editorRetryCount > 0) && (
          <Center position="absolute" top={0} left={0} right={0} bottom={0} zIndex={10} bg="rgba(255,255,255,0.7)">
            <VStack space="sm" alignItems="center">
              <Spinner size="small" />
              <Text size="xs" color="$textSubtle">
                {t('student.task.composer.messages.initializing', 'Initializing editor (Attempt {{count}})...', { count: editorRetryCount })}
              </Text>
            </VStack>
          </Center>
        )}

        {!showReadonlyView && editorStatus === 'error' && (
          <Center position="absolute" top={0} left={0} right={0} bottom={0} zIndex={10} bg="rgba(255,255,255,0.9)">
            <VStack space="md" alignItems="center" p="$6">
              <Icon as={AlertCircle} size="xl" color="$statusError" />
              <Text size="sm" color="$textBody" textAlign="center" fontWeight="bold">
                {t('student.task.composer.errors.init-failed', 'Failed to initialize the editor.')}
              </Text>
              <Text size="xs" color="$textSubtle" textAlign="center">
                {t('student.task.composer.errors.init-failed-hint', 'Please try refreshing the page or check your connection.')}
              </Text>
              <Button size="xs" onPress={() => navigation.goBack()}>
                <ButtonText>{t('common.buttons.go-back', 'Go Back')}</ButtonText>
              </Button>
            </VStack>
          </Center>
        )}

        {showReadonlyView ? (
          <VStack flex={1}>
            <HStack
              bg="$bgSubtle"
              px="$6"
              py="$4"
              justifyContent="space-between"
              alignItems="center"
              borderBottomWidth={1}
              borderColor="$borderLight"
            >
              <HStack space="sm" alignItems="center">
                <Icon as={CircleCheckBig} size="sm" color="$success600" />
                <Text size="sm" color="$textBody" fontWeight="medium">
                  {t('student.task.composer.messages.marked-as-done', 'Marked as done')}
                </Text>
              </HStack>
              <Button
                size="xs"
                variant="outline"
                onPress={handleReopen}
                isDisabled={isTasksLoading}
                borderColor="$borderMedium"
                bg="$white"
              >
                <ButtonIcon as={Pencil} size="xs" mr="$2" color="$textBody" />
                <ButtonText color="$textBody">
                  {t('student.task.composer.buttons.reopen-to-edit', 'Reopen to Edit')}
                </ButtonText>
              </Button>
            </HStack>
            <ScrollView flex={1} p="$6">
              <HtmlText content={content} applySubmissionFormatting />
            </ScrollView>
          </VStack>
        ) : (
          <EditorShell
            testID={testID}
            ref={editorRef}
            initialHtml={content}
            onHtmlChange={(html: string, meta) => {
              const typedMeta = meta as { source?: string } | undefined;
              if (typedMeta && typedMeta.source !== 'user') return;
              setContentAndWordcount(html);
              setDirty(true);
            }}
            onShowHistory={isDraft && !readonly ? handleShowHistory : undefined}
            readonly={readonly || isTasksLoading}
            placeholder={t('student.task.composer.messages.placeholder')}
            borderless={!isMobileView}
          />
        )}
        <AttachmentsPanel
          testID="attachments-panel"
          courseId={selectedModule?.courseId}
          moduleId={task?.moduleId}
          taskId={task?.id}
          attachments={task?.submission?.attachments || []}
          allowedFormats={task?.allowedAttachmentFormats || []}
          readonly={readonly || showReadonlyView}
        />

        <BottomPanel
          show={!isMobileView}
          saveStatus={storeAutosave.status === 'inflight' ? 'saving' : storeAutosave.status}
          lastSavedAt={lastSavedAt}
          errorMessage={storeAutosave.errorMessage}
          isDirty={dirty}
          onRetry={() => saveDraft(undefined, { silent: false, showErrorDialog: true })}
          wordcount={wordcount}
          targetWordcount={wordcountTargets?.target}
          showWordcountDialog={showWordcountDialog}
          wordcountColor={wordcountColor}
          readonly={readonly || showReadonlyView}
          isMarkedAsDone={isMarkedAsDone && !isEditingOverride}
        />
      </VStack>

      {/* Version History Modal */}
      <VersionHistoryModal
        isOpen={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        versions={(historyData?.data?.data as TaskSubmissionVersionSummary[]) || []}
        currentCharCount={content?.length || 0}
        isLoading={isHistoryLoading}
        isError={isHistoryError}
        onLoadVersion={handleLoadVersion}
      />
    </Box>
  );
};

export default EssayComposer;
